import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useActions } from '../../../../../../hooks/useActions'
import { Row, Col, Card, Button, Spin } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import '../../SIMAEntrance/sima.scss'

const SIMA_DURATION = 300

// Link encoded in the bottom "download the SİMA app" QR.
const SIMA_DOWNLOAD_URL = 'https://onelink.to/gdufks'

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60)
  const seconds = time % 60
  return `0${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
}

const SIMAForm: React.FC<any> = ({ simaInProgress, simaQrCode, simaExpired, simaError }) => {
  const { t } = useTranslation()
  const { getSIMALogin, pollSIMAStatus, setSimaExpired, stopSIMAPolling, resetSIMA } = useActions()
  const history = useHistory()
  const [timeLeft, setTimeLeft] = useState(SIMA_DURATION)
  // The QR is no longer fetched on mount — the user must press "QR aktivləşdir"
  // first. `activated` gates the login request and the polling/timer effects.
  const [activated, setActivated] = useState(false)
  const [showDownload, setShowDownload] = useState(false)

  // On mount, clear any stale SIMA state left in Redux from a previous visit
  // (e.g. an expired/errored QR). destroyInactiveTabPane remounts this component
  // when the user returns to the SIMA tab, so without this the expired screen
  // would persist instead of resetting to the initial "activate" state.
  // On unmount, stop the recursive poll loop (tab switch via
  // destroyInactiveTabPane, or leaving the login page) so it can't keep firing
  // status requests or navigate away after the user has left the SIMA tab.
  useEffect(() => {
    resetSIMA()
    return () => {
      stopSIMAPolling()
    }
  }, [])

  useEffect(() => {
    if (!activated || !simaQrCode || simaExpired || simaError) return
    setTimeLeft(SIMA_DURATION)
    pollSIMAStatus(history)
  }, [activated, simaQrCode, simaExpired, simaError])

  useEffect(() => {
    if (!activated || !simaQrCode || simaExpired) return
    setTimeLeft(SIMA_DURATION)
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          setSimaExpired()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [activated, simaQrCode])

  const handleActivate = () => {
    setActivated(true)
    getSIMALogin(history)
  }

  // On expiry the user presses the button to get a fresh QR. Stop any leftover
  // polling, then request a new login straight away — getSIMALogin dispatches
  // SIMA_QR_IN_PROGRESS which clears simaExpired/simaError, so the effects above
  // re-arm the timer and poll loop once the new QR arrives. `activated` stays
  // true so we don't fall back to the placeholder between press and QR arrival.
  const handleReactivate = () => {
    stopSIMAPolling()
    setActivated(true)
    getSIMALogin(history)
  }

  // Show the initial activate state whenever the QR isn't live: before first
  // activation, and after it has expired / errored.
  const showActivate = !activated || simaExpired || simaError

  return (
    <Row style={{ width: '100%', margin: 0 }}>
      <Col style={{ width: '100%' }} className="sima-card">
        <Card bodyStyle={{ padding: '12px 16px' }}>
          {showActivate ? (
            <div className={`sima-card__qr sima-card__qr--activate${simaExpired || simaError ? ' sima-card__qr--expired' : ''}`}>
              <QRCodeSVG
                className="sima-card__qr-placeholder"
                value="https://simaimza.az/placeholder/activate?ref=login&session=00000000-0000-0000-0000-000000000000"
                size={200}
                level="H"
                includeMargin={true}
              />
              <Button
                className="sima-card__activate-btn"
                // type="primary"
                onClick={simaExpired || simaError ? handleReactivate : handleActivate}
              >
                {t("sima.activateQr")}
              </Button>
            </div>
          ) : simaInProgress ? (
            <div className="sima-card__qr sima-card__qr--loader">
              <Spin size="large" />
            </div>
          ) : simaQrCode ? (
            <div className="sima-card__qr">
              <QRCodeSVG value={simaQrCode} size={200} includeMargin={true} />
            </div>
          ) : (
            <div className="sima-card__qr sima-card__qr--loader">
              <Spin size="large" />
            </div>
          )}

          {activated && !simaExpired && !simaError && (
            <>
              <p className="sima-card__expire-label">{t("sima.qrExpiryTime")}</p>
              <div className="sima-card__timer-wrap">
                <span className="sima-card__timer-badge">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </>
          )}

          {(simaExpired || simaError) && (
            <>
              <p className="sima-card__expired-label">
                {simaError ? t("sima.errorOccurred") : t("sima.qrExpired")}
              </p>
              <div className="sima-card__timer-wrap">
                <span className="sima-card__timer-badge sima-card__timer-badge--expired">
                  {formatTime(0)}
                </span>
              </div>
            </>
          )}

          <ol className="sima-card__steps">
            <li><span>1</span>{t("sima.step1")}</li>
            <li><span>2</span>{t("sima.step2")}</li>
            <li><span>3</span>{t("sima.step3Login")}</li>
          </ol>

          <div className="sima-card__download">
            <button
              type="button"
              className="sima-card__download-toggle"
              onClick={() => setShowDownload(prev => !prev)}
            >
              {t("sima.noApp")}
              <DownOutlined className={`sima-card__download-caret${showDownload ? ' sima-card__download-caret--open' : ''}`} />
            </button>
            {showDownload && (
              <div className="sima-card__download-panel">
                <div className="sima-card__download-qr">
                  <QRCodeSVG value={SIMA_DOWNLOAD_URL} size={72} />
                </div>
                <span className="sima-card__download-text">
                  {t("sima.scanToDownload")}
                </span>
              </div>
            )}
          </div>

          <p className="sima-card__note">
            {t("sima.errorHelp")}
          </p>
        </Card>
      </Col>
    </Row>
  )
}

const mapStateToProps = (state: any) => ({
  simaInProgress: state.token.simaInProgress,
  simaQrCode: state.token.simaQrCode,
  simaExpired: state.token.simaExpired,
  simaError: state.token.simaError,
})

export default connect(mapStateToProps)(SIMAForm)
